/**
 * Volume / mix-and-match offer (POC).
 * Checkout discount: temporary native Shopify automatic discounts on the eligible collection.
 * Later: read offer JSON from Switchboard public metafield; remove native Admin discounts.
 */
if (!customElements.get("bundle-volume-offer")) {
  customElements.define(
    "bundle-volume-offer",
    class BundleVolumeOffer extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.sectionId = this.dataset.sectionId;
        this.productFormId = this.dataset.productFormId;
        this.hideQuantity = this.dataset.hideQuantity === "true";
        this.moneyFormat = this.dataset.moneyFormat || "${{amount}}";
        this.unitPrice = Number(this.dataset.unitPrice || 0);
        this.variantId = this.dataset.variantId;

        this.tiersEl = this.querySelector(".bundle-volume-offer__tiers");
        this.slotsEl = this.querySelector("[data-slots]");
        this.slotList = this.querySelector("[data-slot-list]");
        this.dialog = this.querySelector("[data-picker-dialog]");
        this.strings = window.bundleVolumeStrings || {};

        try {
          this.currentProduct = JSON.parse(
            this.querySelector("[data-current-product]")?.textContent || "{}",
          );
        } catch (e) {
          this.currentProduct = {
            productId: this.dataset.productId,
            variantId: this.variantId,
            title: "",
            price: this.unitPrice,
            image: null,
          };
        }

        this.activeSlotIndex = null;
        this.slots = [];

        this.onTierClick = this.onTierClick.bind(this);
        this.onSlotChangeClick = this.onSlotChangeClick.bind(this);
        this.onPick = this.onPick.bind(this);
        this.onFormSubmit = this.onFormSubmit.bind(this);
        this.closePicker = this.closePicker.bind(this);

        this.addEventListener("click", this.onTierClick);
        this.slotList?.addEventListener("click", this.onSlotChangeClick);
        this.querySelector("[data-picker-grid]")?.addEventListener(
          "click",
          this.onPick,
        );
        this.querySelector("[data-picker-close]")?.addEventListener(
          "click",
          this.closePicker,
        );
        this.dialog?.addEventListener("cancel", this.closePicker);

        this.productInfo = this.closest("product-info");
        this.form =
          document.getElementById(this.productFormId) ||
          this.productInfo?.querySelector('form[data-type="add-to-cart-form"]');
        if (this.form) {
          this.form.addEventListener("submit", this.onFormSubmit, true);
        }

        if (this.hideQuantity && this.productInfo) {
          this.productInfo.classList.add("product-info--bundle-qty-hidden");
        }

        this.variantChangeUnsubscriber = subscribe?.(
          PUB_SUB_EVENTS.variantChange,
          (event) => {
            const variant = event?.data?.variant;
            if (!variant) return;
            this.variantId = String(variant.id);
            this.unitPrice = Number(variant.price || 0);
            this.dataset.variantId = this.variantId;
            this.dataset.unitPrice = String(this.unitPrice);
            this.currentProduct = {
              ...this.currentProduct,
              variantId: this.variantId,
              price: this.unitPrice,
            };
            if (this.slots[0]) {
              this.slots[0] = {
                ...this.slots[0],
                variantId: this.variantId,
                price: this.unitPrice,
              };
              this.renderSlots();
            }
            this.updateTierPrices();
            this.syncQuantityInput();
            this.dispatchTierChange();
          },
        );

        const selected =
          this.querySelector(".bundle-volume-offer__tier.is-selected") ||
          this.querySelector(".bundle-volume-offer__tier");
        if (selected) this.selectTier(selected, false);
      }

      disconnectedCallback() {
        this.removeEventListener("click", this.onTierClick);
        this.form?.removeEventListener("submit", this.onFormSubmit, true);
        this.variantChangeUnsubscriber?.();
        if (this.productInfo) {
          this.productInfo.classList.remove("product-info--bundle-qty-hidden");
        }
      }

      onTierClick(event) {
        const tier = event.target.closest(".bundle-volume-offer__tier");
        if (!tier || !this.contains(tier)) return;
        // Ignore clicks inside the product picker dialog / slot change controls.
        if (
          event.target.closest(
            "[data-picker-dialog], [data-change-slot], [data-pick]",
          )
        ) {
          return;
        }
        this.selectTier(tier, true);
      }

      selectTier(tierButton, focusSlots) {
        this.querySelectorAll(".bundle-volume-offer__tier").forEach((btn) => {
          const selected = btn === tierButton;
          btn.classList.toggle("is-selected", selected);
          btn.setAttribute("aria-checked", selected ? "true" : "false");
        });

        this.selectedQty = Number(tierButton.dataset.qty || 1);
        this.selectedFinalPrice = Number(tierButton.dataset.finalPrice || 0);
        this.selectedLabel =
          tierButton
            .querySelector(".bundle-volume-offer__tier-label")
            ?.textContent.trim() || "";
        this.rebuildSlots(this.selectedQty);
        this.syncQuantityInput();
        this.updateTierPrices();
        this.dispatchTierChange();

        if (this.selectedQty > 1) {
          this.slotsEl?.removeAttribute("hidden");
          if (focusSlots) this.slotList?.querySelector("button")?.focus();
        } else {
          this.slotsEl?.setAttribute("hidden", "");
        }
      }

      dispatchTierChange() {
        const selected = this.querySelector(
          ".bundle-volume-offer__tier.is-selected",
        );
        const priceText =
          selected?.querySelector("[data-tier-sale]")?.textContent.trim() || "";
        const perPairText =
          selected?.querySelector("[data-tier-per-pair]")?.textContent.trim() ||
          "";
        const compareEl = selected?.querySelector("[data-tier-compare]");
        const hasCompare =
          Boolean(compareEl) && !compareEl.classList.contains("is-empty");
        const compareText = hasCompare ? compareEl.textContent.trim() : "";
        const qty = this.selectedQty || 1;
        const finalPrice = Number(this.selectedFinalPrice || 0);
        const priceCents = finalPrice > 0 ? finalPrice : this.unitPrice * qty;

        this.dispatchEvent(
          new CustomEvent("bundle:tierchange", {
            bubbles: true,
            detail: {
              label: this.selectedLabel || "",
              qty,
              priceCents,
              priceText,
              perPairText,
              compareText,
              hasCompare,
            },
          }),
        );
      }

      rebuildSlots(qty) {
        const next = [];
        for (let i = 0; i < qty; i++) {
          next.push(
            this.slots[i] || {
              productId: this.currentProduct.productId,
              variantId: String(
                this.currentProduct.variantId || this.variantId,
              ),
              title: this.currentProduct.title,
              price: Number(this.currentProduct.price || this.unitPrice),
              image: this.currentProduct.image,
            },
          );
        }
        // Keep first slot tied to the PDP product/variant when possible
        next[0] = {
          ...next[0],
          productId: this.currentProduct.productId,
          variantId: String(this.currentProduct.variantId || this.variantId),
          title: this.currentProduct.title || next[0].title,
          price: Number(this.currentProduct.price || this.unitPrice),
          image: this.currentProduct.image || next[0].image,
        };
        this.slots = next;
        this.renderSlots();
      }

      renderSlots() {
        if (!this.slotList) return;
        const changeLabel = this.strings.changeProduct || "Change";
        this.slotList.innerHTML = this.slots
          .map((slot, index) => {
            const img = slot.image
              ? `<img class="bundle-volume-offer__slot-image" src="${this.escapeAttr(slot.image)}" alt="" width="56" height="56" loading="lazy">`
              : `<span class="bundle-volume-offer__slot-placeholder" aria-hidden="true"></span>`;
            const slotLabel = (
              this.strings.slotLabel || "Pair {{ index }}"
            ).replace("{{ index }}", String(index + 1));
            return `<li class="bundle-volume-offer__slot" data-slot-index="${index}">
              ${img}
              <div class="bundle-volume-offer__slot-meta">
                <span class="bundle-volume-offer__slot-index">${this.escapeHtml(slotLabel)}</span>
                <span class="bundle-volume-offer__slot-title">${this.escapeHtml(slot.title || "")}</span>
              </div>
              <button type="button" class="bundle-volume-offer__slot-change" data-change-slot="${index}">${this.escapeHtml(changeLabel)}</button>
            </li>`;
          })
          .join("");
      }

      onSlotChangeClick(event) {
        const button = event.target.closest("[data-change-slot]");
        if (!button) return;
        this.activeSlotIndex = Number(button.dataset.changeSlot);
        this.openPicker();
      }

      openPicker() {
        if (!this.dialog) return;
        if (typeof this.dialog.showModal === "function") {
          this.dialog.showModal();
        } else {
          this.dialog.setAttribute("open", "");
        }
      }

      closePicker(event) {
        event?.preventDefault?.();
        if (!this.dialog) return;
        if (typeof this.dialog.close === "function") {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute("open");
        }
        this.activeSlotIndex = null;
      }

      onPick(event) {
        const pick = event.target.closest("[data-pick]");
        if (!pick || pick.disabled || this.activeSlotIndex == null) return;

        this.slots[this.activeSlotIndex] = {
          productId: pick.dataset.productId,
          variantId: pick.dataset.variantId,
          title: pick.dataset.title,
          price: Number(pick.dataset.price || 0),
          image: pick.dataset.image || null,
        };
        this.renderSlots();
        this.updateTierPrices();
        this.dispatchTierChange();
        this.closePicker();
      }

      syncQuantityInput() {
        const qtyInput =
          document.getElementById(`Quantity-${this.sectionId}`) ||
          this.form?.querySelector('[name="quantity"]');
        if (qtyInput && this.selectedQty) {
          qtyInput.value = String(this.selectedQty);
          qtyInput.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      updateTierPrices() {
        // Compare = current PDP unit price × qty. Sale = merchant-set final price (cents).
        this.querySelectorAll(".bundle-volume-offer__tier").forEach((tier) => {
          const qty = Number(tier.dataset.qty || 1);
          const finalPrice = Number(tier.dataset.finalPrice || 0);
          const compare = this.unitPrice * qty;
          const sale = finalPrice > 0 ? finalPrice : compare;
          const saved = Math.max(0, compare - sale);
          const hasSavings = finalPrice > 0 && saved > 0;
          const perPair = Math.round(sale / Math.max(qty, 1));
          const saleEl = tier.querySelector("[data-tier-sale]");
          const compareEl = tier.querySelector("[data-tier-compare]");
          const perPairEl = tier.querySelector("[data-tier-per-pair]");

          if (saleEl) saleEl.textContent = this.formatMoney(sale);

          if (perPairEl) {
            const template = this.strings.pricePerPair || "{{ price }}/pair";
            perPairEl.textContent = template.replace(
              "{{ price }}",
              this.formatMoney(perPair),
            );
          }

          if (hasSavings) {
            if (compareEl) {
              compareEl.textContent = this.formatMoney(compare);
              compareEl.classList.remove("is-empty");
              compareEl.removeAttribute("aria-hidden");
            }
          } else if (compareEl) {
            compareEl.innerHTML = "&nbsp;";
            compareEl.classList.add("is-empty");
            compareEl.setAttribute("aria-hidden", "true");
          }
        });
      }

      onFormSubmit(event) {
        if (!this.selectedQty || this.selectedQty < 1) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        this.addMixedItems();
      }

      async addMixedItems() {
        const productForm = this.form?.closest("product-form");
        const submitButton = productForm?.querySelector('[type="submit"]');
        const spinner = productForm?.querySelector(".loading__spinner");
        const cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");

        const itemsMap = new Map();
        this.slots.forEach((slot) => {
          const id = Number(slot.variantId);
          if (!id) return;
          itemsMap.set(id, (itemsMap.get(id) || 0) + 1);
        });

        const items = Array.from(itemsMap.entries()).map(([id, quantity]) => ({
          id,
          quantity,
          properties: {
            _bundle_tier: String(this.selectedQty),
            _bundle_final_price: String(this.selectedFinalPrice || 0),
          },
        }));

        if (!items.length) return;

        submitButton?.setAttribute("aria-disabled", "true");
        submitButton?.classList.add("loading");
        spinner?.classList.remove("hidden");

        const body = { items };
        if (cart?.getSectionsToRender) {
          body.sections = cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(",");
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(document.activeElement);
        }

        try {
          const response = await fetch(window.routes.cart_add_url, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              "X-Requested-With": "XMLHttpRequest",
            },
            body: JSON.stringify(body),
          });
          const data = await response.json();

          if (data.status) {
            productForm?.handleErrorMessage?.(data.description || data.message);
            return;
          }

          if (!cart) {
            window.location = window.routes.cart_url;
            return;
          }

          publish?.(PUB_SUB_EVENTS.cartUpdate, {
            source: "bundle-volume-offer",
            cartData: data,
          });

          const payload = await this.normalizeCartAddResponse(data, cart);
          this.renderCartContents(cart, payload);
          cart.classList.remove("is-empty");
        } catch (error) {
          console.error(error);
          productForm?.handleErrorMessage?.(error.message || "Error");
        } finally {
          submitButton?.classList.remove("loading");
          submitButton?.removeAttribute("aria-disabled");
          spinner?.classList.add("hidden");
        }
      }

      async normalizeCartAddResponse(data, cart) {
        const items = Array.isArray(data.items)
          ? data.items
          : data.key
            ? [data]
            : [];
        const added = items[items.length - 1] || data;
        let sections = data.sections;

        const hasSections = sections && Object.keys(sections).length > 0;
        if (!hasSections && cart?.getSectionsToRender) {
          const ids = cart.getSectionsToRender().map((section) => section.id);
          try {
            const root = window.shopUrl || window.location.origin;
            const response = await fetch(`${root}/?sections=${ids.join(",")}`);
            sections = await response.json();
          } catch (error) {
            console.error(error);
            sections = {};
          }
        }

        return {
          ...data,
          key: data.key || added.key,
          itemKeys: items.map((item) => item.key).filter(Boolean),
          bundleTitle: this.selectedLabel || "",
          sections: sections || {},
          id: data.id || added.id,
        };
      }

      renderCartContents(cart, payload) {
        if (typeof cart.renderContents !== "function") {
          window.location = window.routes.cart_url;
          return;
        }

        try {
          cart.renderContents(payload);
        } catch (error) {
          console.error(error);
          cart.open?.();
        }
      }

      formatMoney(cents) {
        const value = (Number(cents) / 100).toFixed(2);
        const format = this.moneyFormat || "${{amount}}";
        const withComma = value.replace(".", ",");
        const noDecimals = String(Math.round(Number(cents) / 100));
        return format
          .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, withComma)
          .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
          .replace(/\{\{\s*amount\s*\}\}/g, value);
      }

      escapeHtml(value) {
        return String(value)
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/"/g, "&quot;");
      }

      escapeAttr(value) {
        return this.escapeHtml(value).replace(/'/g, "&#39;");
      }
    },
  );
}
